
import { Component, Prop } from 'vue-property-decorator';
import NodeAssessment from '@/components/coach/assessment/NodeAssessment.vue';
import { SchemeNodeModel } from '@/models/schemeNodeModel';
import ViewSkillModal from '@/components/coach/scheme/ViewSkillModal.vue';
import {
  findManualButtonNode,
  getForcePassed,
  isManualLevel,
  nodeRequirementsMet,
  isManualButtonId,
  isAssessible,
} from '@/services/utils';
import { GroupSchemeScope } from '@/components/mixins/schemescope';
import { ListOpenFirst } from '@/components/mixins/listOpenFirst';
import { mixins } from 'vue-class-component';

@Component({
  components: {
    NodeAssessment,
    ViewSkillModal,
  },
  name: 'MemberAssessmentTree',
})
export default class MemberAssessmentTree extends mixins(GroupSchemeScope, ListOpenFirst) {
  /**
   * assessmentTree
   * This is the scheme nodes a member can be assessed by.
   * It is constructed by filtering the scheme by the members available nodes.
   *
   * @private
   * @property {SchemeNodeModel}
   */
  @Prop() assessmentTree!: Array<SchemeNodeModel>;

  /**
   * memberUuid
   * Current members UUID.
   *
   * @private
   * @property {string}
   */
  @Prop() private memberUuid!: string;

  /**
   * dialogOpen
   * Controls visibility of the v-dialog component.
   *
   * @private
   * @property {boolean}
   */
  private dialogOpen = false;

  /**
   * dialogSelectedElement
   * This is updated when the dialog is opened and will contain the ID of the selected scheme node.
   *
   * @private
   * @property {string}
   */
  private dialogSelectedElement = '';

  private passAllLoading = false;

  beforeMount() {
    this.loadPlannedSkills();
  }

  /**
   * memberUuid
   * Current members UUID.
   *
   * @param {string} nodeId
   * @return {void}
   */
  showDialog(nodeId: string): void {
    this.dialogOpen = true;
    this.dialogSelectedElement = nodeId;
  }

  isAssessible(node: SchemeNodeModel): boolean {
    return isAssessible(node);
  }

  /**
   * Computed getter for current members assessment tree.
   *
   * @return {SchemeNodeModel}
   */
  get assessmentSchemeTree(): Array<SchemeNodeModel> {
    const forcePassedNodes = this.forcePassedNodes;
    return this.assessmentTree.filter(n => !forcePassedNodes.includes(n.id));
  }

  get member() {
    return this.$store.getters['groupMembers/memberById'](this.groupId, this.memberUuid);
  }

  get forcePassedNodes(): Array<string> {
    // PERF: available perf improvement, this is called multiple times when viewing a member. Cache.
    return getForcePassed(this.scheme, this.member);
  }

  get passAllButtonText() {
    return this.passAllLoading ? this.$t('global.saving') : this.$t('members.pass_all');
  }

  /**
   * Checks if the given node has children that are assessable.
   *
   * @param {SchemeNodeModel} node
   * @return {boolean}
   */
  nodeHasAssessableChildren(node: SchemeNodeModel): boolean {
    if (!node.children) {
      return false;
    }
    return node.children.filter(isAssessible).length > 0;
  }

  /**
   * Formats each nodes name suitable for URL display, it is then attached to that skills link as a param.
   *
   * @param {SchemeNodeModel} node
   * @return {string}
   */
  getNodeLinkName(node: SchemeNodeModel): string {
    return encodeURIComponent(node.title.replace(/\s+/g, '-').toLowerCase());
  }

  /**
   * Returns the nearest parent that is a level to the given node.
   *
   * @param {SchemeNodeModel} node
   * @return {SchemeNodeModel}
   */
  getNodesNearestLevelParent(node: SchemeNodeModel): SchemeNodeModel {
    let parent = this.getParentByID(node);
    while (parent && parent.grade) {
      parent = this.getParentByID(parent);
    }
    return parent;
  }

  /**
   * Returns the given nodes parent from the store.
   *
   * @param {SchemeNodeModel} node
   * @return {SchemeNodeModel}
   */
  getParentByID(node: SchemeNodeModel): SchemeNodeModel {
    return node.parentId ? this.getNode(node.parentId) : ({} as SchemeNodeModel);
  }

  completedMemberAssessments(node: SchemeNodeModel): string {
    return this.getMemberAssessmentsComplete(node, this.member, this.forcePassedNodes.includes(node.id));
  }

  isManualLevel(node: SchemeNodeModel) {
    return isManualLevel(node);
  }

  isManualButtonId(nodeId: string) {
    return isManualButtonId(nodeId);
  }

  manualLevelIsPassed(node: SchemeNodeModel) {
    const button = findManualButtonNode(this.getNode(node.id));
    if (!button) {
      return false;
    }

    return button.id in this.member.assessments.complete && this.member.assessments.complete[button.id] === true;
  }

  isPassable(node: SchemeNodeModel) {
    const button = findManualButtonNode(this.getNode(node.id));
    if (!button) {
      return false;
    }
    return nodeRequirementsMet(button.dependencies, this.member.assessments.complete);
  }

  // Pass all skills for a level
  async passAll(node: SchemeNodeModel) {
    const res = await this.$confirm(this.$t('members.pass_all_member_confirm'));
    if (!res) {
      return;
    }
    this.passAllLoading = true;

    // This is to allow the modal to close so we can display the loading icon on the pass all button
    setTimeout(async () => {
      if (await this.passNodeChildren(node)) {
        this.$alert('success', this.$t('members.pass_all_skills_for_n', { skill: node.title }) as string);
      } else {
        this.$alert('error', this.$t('members.error_pass_all_skills_for_n', { skill: node.title }) as string);
      }
      this.passAllLoading = false;
    }, 1000);
  }

  async passNodeChildren(node: SchemeNodeModel): Promise<boolean> {
    // pass all skills under this node, recursively
    if (isAssessible(node)) {
      return await this.passSkill(this.member.uuid, node);
    }
    return (await Promise.all(node.children.map(this.passNodeChildren))).every(v => v === true);
  }

  forcePassedNode(node: SchemeNodeModel) {
    return this.forcePassedNodes.includes(node.id);
  }

  allPassed(node: SchemeNodeModel) {
    return node.children.every(node => node.id in this.member.assessments.complete);
  }

  canPassAll(node: SchemeNodeModel) {
    // If not a manual level and is displayed then should be at least 1 assessable node
    if (!isManualLevel(node)) {
      return true;
    }
    return node.children.some(c => isAssessible(c) && !isManualButtonId(c.id));
  }
}
